import { useRef } from "react";
import { T } from "../../../app/tokens.js";
import { mediaLabels } from "./mediaDisplay.js";

// Module scope covers drags between different gallery instances/parents too.
let internalDrag = false;
export function markGalleryDrag(event) {
  internalDrag = true;
  event.dataTransfer?.setData?.("application/x-places-gallery", "internal");
}
export function endGalleryDrag() { internalDrag = false; }
export function isGalleryDrag(event) {
  return internalDrag || [...(event.dataTransfer?.types || [])].includes("application/x-places-gallery");
}
export function MediaReorderTile({ item, items, coverId, canReorder, busy, onReorder, children, ...props }) {
  const touch = useRef(null);
  const node = useRef(null);
  const pinned = String(item.id) === String(coverId);
  const enabled = canReorder && !busy && !pinned;
  const moveTo = target => {
    if (!enabled || target == null || String(target) === String(coverId)) return;
    const next = items.map(i => String(i.id));
    const from = next.indexOf(String(item.id)), to = next.indexOf(String(target));
    if (from < 0 || to < 0 || from === to) return;
    next.splice(to,0,...next.splice(from,1));
    onReorder(next, item.id, () => node.current?.focus({ preventScroll: true }));
  };
  return <div {...props} ref={node} data-media-id={item.id} tabIndex={canReorder ? 0 : undefined}
    aria-label={canReorder ? `${mediaLabels(item).primary}${pinned ? ", Cover pinned first" : ", Alt plus arrow keys to reorder"}` : undefined}
    draggable={Boolean(enabled)}
    onDragStart={event => {
      markGalleryDrag(event);
      if (!enabled) { event.preventDefault(); endGalleryDrag(); return; }
      event.dataTransfer.setData("application/x-places-media-id",String(item.id));
      event.dataTransfer.effectAllowed = "move";
    }}
    onDragEnd={endGalleryDrag}
    onDragOver={event => { if (isGalleryDrag(event)) { event.preventDefault(); event.stopPropagation(); } }}
    onDrop={event => {
      if (!isGalleryDrag(event)) return;
      event.preventDefault(); event.stopPropagation();
      const id = event.dataTransfer?.getData?.("application/x-places-media-id");
      if (canReorder && !busy && !pinned && items.some(i => String(i.id) === id) && id !== String(coverId) && id !== String(item.id)) {
        const next = items.map(i => String(i.id)), from = next.indexOf(id), to = next.indexOf(String(item.id));
        next.splice(to,0,...next.splice(from,1));
        onReorder(next,id,() => [...(node.current?.parentElement?.children || [])].find(n => n.dataset.mediaId === id)?.focus({preventScroll:true}));
      }
      endGalleryDrag();
    }}
    onKeyDown={event => {
      if (!event.altKey || !["ArrowLeft","ArrowRight","ArrowUp","ArrowDown"].includes(event.key) || !enabled) return;
      event.preventDefault(); event.stopPropagation();
      const index = items.findIndex(i => i.id === item.id);
      moveTo(items[index + (["ArrowLeft","ArrowUp"].includes(event.key) ? -1 : 1)]?.id);
    }}>
    {children}
    {canReorder && !pinned && <button type="button" data-reorder-handle aria-label={`Drag to reorder ${mediaLabels(item).primary}`} disabled={busy}
      style={{ touchAction: "none", cursor: "grab", color: T.mid, background: T.card, border: 0, padding: "4px 9px", fontSize: T.fsXs }}
      onPointerDown={event => {
        if (!enabled || (event.button != null && event.button !== 0)) return;
        event.preventDefault(); event.stopPropagation();
        touch.current = { x:event.clientX,y:event.clientY,id:event.pointerId };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        node.current?.focus({ preventScroll: true });
      }}
      onPointerUp={event => {
        const start = touch.current; touch.current = null;
        if (!start || start.id !== event.pointerId) return;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        if (Math.hypot(event.clientX-start.x,event.clientY-start.y) < 8) return;
        const target = document.elementFromPoint?.(event.clientX,event.clientY)?.closest("[data-media-id]");
        // The ID must also belong to this gallery, checked by moveTo.
        moveTo(target?.dataset.mediaId);
      }}
      onPointerCancel={() => { touch.current = null; }}>Reorder ↔</button>}
  </div>;
}
